import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { DragProvider, ReorderableList } from "@activecollab/yank-and-drop";
import classnames from "classnames";
import update from "immutability-helper";
import PropTypes from "prop-types";
import { completeSubtask, reorder } from "~/store/actions/actions.subtasks.jsx";
import { getOpenSubtasksByTaskId } from "~/store/selectors/selector.subtasks.jsx";
import { hasLoggedUserElevatedPermissions } from "~/store/selectors/selector.users.jsx";
import { DraggableSubtaskItem } from "./DraggableSubtaskItem.js";
import SubtaskItemContainer from "./SubtaskItemContainer.jsx";

const SubtaskList = ({ taskId, projectId, onConvertToTask, convertToTaskReact }) => {
  const dispatch = useDispatch();
  const lastReorderRef = useRef(null);
  const [editSubtaskId, setEditSubtaskId] = useState(0);

  const subtaskIds = useSelector((state) => getOpenSubtasksByTaskId(state, taskId));
  const [items, setItems] = useState(() => subtaskIds);
  const [reordering, setReordering] = useState(false);

  const renderItems = useMemo(() => {
    if (reordering) return items;
    return subtaskIds;
  }, [items, reordering, subtaskIds]);

  useUpdateEffect(() => {
    setItems(subtaskIds);
  }, [subtaskIds]);

  const editable = useSelector(hasLoggedUserElevatedPermissions);

  const onCompleteSubtask = useCallback(
    (id) => {
      return dispatch(completeSubtask(id));
    },
    [dispatch]
  );

  const handleStartEdit = useCallback((id) => {
    setEditSubtaskId(id);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditSubtaskId(0);
  }, []);

  const handleMove = useCallback((dragIndex, hoverIndex) => {
    setReordering(true);
    setItems((prevState) => {
      lastReorderRef.current = {
        source: prevState[dragIndex],
        target: prevState[hoverIndex],
        before: dragIndex > hoverIndex,
      };
      return update(prevState, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevState[dragIndex]],
        ],
      });
    });
  }, []);

  const handleSave = useCallback(() => {
    if (lastReorderRef.current?.source !== lastReorderRef.current?.target) {
      dispatch(
        reorder(
          projectId,
          taskId,
          lastReorderRef.current.source,
          lastReorderRef.current.target,
          lastReorderRef.current.before
        )
      ).then(() => {
        setReordering(false);
      });
    }
  }, [dispatch, projectId, taskId]);

  return subtaskIds.length ? (
    <div className="task_list subtask_list">
      <div
        className={classnames(`tasks sortable_enabled`, {
          editable,
        })}
      >
        <div
          style={{
            marginLeft: !editable ? 0 : subtaskIds.length > 1 ? "-31px" : "-8px",
            marginBottom: editable ? "4px" : 0,
          }}
        >
          <DragProvider>
            <ReorderableList
              onReorder={handleSave}
              dragAxis="y"
              handle=".drag-handle"
              onMove={handleMove}
            >
              {renderItems.map((id) => (
                <DraggableSubtaskItem
                  key={`subtask-${id}`}
                  disabled={!editable || subtaskIds.length === 1}
                >
                  <SubtaskItemContainer
                    key={`subtask-${id}`}
                    id={id}
                    projectId={projectId}
                    taskId={taskId}
                    editMode={editSubtaskId === id}
                    onComplete={onCompleteSubtask}
                    onConvertToTask={onConvertToTask}
                    convertToTaskReact={convertToTaskReact}
                    onStartEdit={handleStartEdit}
                    onCancelEdit={handleCancelEdit}
                    canModify={editable}
                  />
                </DraggableSubtaskItem>
              ))}
            </ReorderableList>
          </DragProvider>
        </div>
      </div>
    </div>
  ) : null;
};

SubtaskList.propTypes = {
  taskId: PropTypes.number,
  projectId: PropTypes.number,
  onConvertToTask: PropTypes.func,
  convertToTaskReact: PropTypes.func,
};

export default SubtaskList;
